/**
 * CorporateMagicLinkVerify — Phase 101et (Feb 2026)
 *
 * Page cible du lien magique envoyé par Resend. Valide le JWT côté serveur,
 * pose un cookie de session Corporate (24h) puis redirige vers /corporate/{id}.
 */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";

export default function CorporateMagicLinkVerify() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const token = sp.get("token") || "";
  const distId = sp.get("dist_id") || "";
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!token || !distId) {
      setStatus("bad_request");
      return;
    }
    (async () => {
      try {
        await api.post(`/public/corporate/verify-magic-link?token=${encodeURIComponent(token)}&dist_id=${encodeURIComponent(distId)}`);
        setStatus("ok");
        // Redirection après un très court délai — laisse le temps au cookie de se poser
        setTimeout(() => navigate(`/corporate/${distId}`, { replace: true }), 800);
      } catch (e) {
        setStatus(e?.response?.status === 400 ? "expired" : "error");
      }
    })();
  }, [token, distId, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBF4E9] p-6">
      <div className="w-full max-w-md bg-white shadow-lg rounded-lg overflow-hidden text-center" data-testid="corporate-magic-verify">
        <div className="px-8 py-10 space-y-4">
          {status === "loading" && (
            <>
              <Loader2 className="w-10 h-10 text-[#5B3A8C] mx-auto animate-spin" />
              <p className="text-sm text-[#575A57]">Vérification du lien en cours…</p>
            </>
          )}
          {status === "ok" && (
            <>
              <ShieldCheck className="w-12 h-12 text-[#88A066] mx-auto" />
              <p className="text-base font-semibold text-[#1A1C1A]">Accès confirmé</p>
              <p className="text-xs text-[#575A57]">Redirection vers le catalogue…</p>
            </>
          )}
          {status === "expired" && (
            <>
              <XCircle className="w-12 h-12 text-[#C05C47] mx-auto" />
              <p className="text-base font-semibold text-[#1A1C1A]">Lien expiré ou invalide</p>
              <p className="text-sm text-[#575A57]">Merci de redemander un nouveau lien d'accès.</p>
              <Button onClick={() => navigate(`/corporate/${distId}`)} className="bg-[#5B3A8C] hover:bg-[#3F286B]" data-testid="magic-retry">
                Redemander un lien
              </Button>
            </>
          )}
          {status === "bad_request" && (
            <>
              <XCircle className="w-12 h-12 text-[#C05C47] mx-auto" />
              <p className="text-base font-semibold text-[#1A1C1A]">Lien incomplet</p>
              <p className="text-sm text-[#575A57]">Vérifiez que vous avez cliqué sur le lien complet reçu par email.</p>
            </>
          )}
          {status === "error" && (
            <>
              <XCircle className="w-12 h-12 text-[#C05C47] mx-auto" />
              <p className="text-base font-semibold text-[#1A1C1A]">Erreur inattendue</p>
              <p className="text-sm text-[#575A57]">Réessayez ou contactez Maison Thaï.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
